import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  MessagesSquare, Plus, ThumbsUp, MessageSquare, Eye, ArrowLeft, Trash2,
  Edit3, Loader2, Send, X, Check, Pin, PinOff, Search, Laugh, ImagePlus, Plane, Reply,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatMSTDate, formatMSTDateTime } from "@/lib/timeUtils";

const CATS = {
  general: { label: "General", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  maintenance: { label: "Maintenance", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  safety: { label: "Safety", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  announcements: { label: "Announcements", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  helping_hand: { label: "Helping Hand", cls: "bg-purple-100 text-purple-800 border-purple-200" },
};

const EMOJIS = ["👍", "🙏", "😀", "😂", "🎉", "❤️", "😮", "😢", "💪", "🔧", "✈️", "✅"];

const PAGE_SIZE = 15;

function EmojiBar({ onPick, prefix }) {
  return (
    <div className="flex items-center gap-0.5 flex-wrap" data-testid={`${prefix}-emoji-bar`}>
      {EMOJIS.map((e, i) => (
        <button
          key={e}
          type="button"
          onClick={() => onPick(e)}
          data-testid={`${prefix}-emoji-${i}`}
          className="w-7 h-7 rounded hover:bg-summit-glacier text-base leading-none grid place-items-center transition-colors"
        >
          {e}
        </button>
      ))}
    </div>
  );
}

function PinBadge({ testId }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest bg-summit-primary text-white border-summit-primary" data-testid={testId}>
      <Pin className="w-3 h-3" /> Pinned
    </span>
  );
}

function TailBadge({ tail, testId }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest bg-gray-100 text-gray-700 border-gray-200" data-testid={testId}>
      <Plane className="w-3 h-3" /> {tail}
    </span>
  );
}

const photoUrl = (pid) => `${API_BASE}/discussions/photos/${pid}`;

function HahaButton({ reacted, count, onClick, testId, small }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border transition-colors ${small ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"} ${
        reacted ? "bg-amber-50 border-amber-300 text-amber-700" : "border-summit-hairline text-gray-500 hover:bg-summit-glacier"
      }`}
    >
      <Laugh className={`${small ? "w-3 h-3" : "w-3.5 h-3.5"} ${reacted ? "text-amber-600" : ""}`} />
      <span className="font-mono tabular-nums">{count}</span>
    </button>
  );
}

function PhotoStrip({ photos, size = "h-28" }) {
  if (!photos?.length) return null;
  return (
    <div className="flex gap-2 mt-2.5 flex-wrap">
      {photos.map((p) => (
        <a key={p} href={photoUrl(p)} target="_blank" rel="noreferrer" data-testid={`disc-photo-${p}`}>
          <img src={photoUrl(p)} alt="attachment" className={`${size} rounded-md border border-summit-hairline object-cover hover:opacity-90 transition-opacity`} />
        </a>
      ))}
    </div>
  );
}

function PhotoPicker({ photos, setPhotos, prefix }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= 3) { toast.error("Max 3 photos"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/discussions/photos", fd);
      setPhotos([...photos, r.data.id]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {photos.map((p) => (
        <div key={p} className="relative">
          <img src={photoUrl(p)} alt="" className="h-16 w-16 rounded-md border border-summit-hairline object-cover" />
          <button
            type="button"
            onClick={() => setPhotos(photos.filter((x) => x !== p))}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-800 text-white rounded-full grid place-items-center hover:bg-rose-600 transition-colors"
            data-testid={`${prefix}-photo-remove-${p}`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading || photos.length >= 3}
        className="h-16 w-16 rounded-md border border-dashed border-summit-hairline grid place-items-center text-gray-400 hover:text-summit-primary hover:border-summit-primary/50 transition-colors disabled:opacity-50"
        data-testid={`${prefix}-photo-add`}
        title="Attach photo"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${prefix}-photo-input`} />
    </div>
  );
}

const ago = (iso) => {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return formatMSTDate(iso);
};

const initials = (name) => {
  const [last = "?", first = ""] = (name || "").split(", ");
  return `${(first[0] || last[0] || "?")}${first ? last[0] || "" : ""}`.toUpperCase();
};

function CatBadge({ cat }) {
  const meta = CATS[cat] || CATS.general;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function LikeButton({ liked, count, onClick, testId, small }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border transition-colors ${small ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"} ${
        liked ? "bg-sky-50 border-sky-300 text-sky-700" : "border-summit-hairline text-gray-500 hover:bg-summit-glacier"
      }`}
    >
      <ThumbsUp className={`${small ? "w-3 h-3" : "w-3.5 h-3.5"} ${liked ? "fill-sky-500 text-sky-600" : ""}`} />
      <span className="font-mono tabular-nums">{count}</span>
    </button>
  );
}

function TopicDialog({ open, onClose, onSaved, editing }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("general");
  const [tail, setTail] = useState("none");
  const [photos, setPhotos] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(editing?.title || "");
      setBody(editing?.body || "");
      setCategory(editing?.category || "general");
      setTail(editing?.tail || "none");
      setPhotos(editing?.photos || []);
      if (aircraft.length === 0) {
        api.get("/aircraft").then((r) => setAircraft(r.data || [])).catch(() => {});
      }
    }
  }, [open, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    const payload = { title, body, category, tail: tail === "none" ? "" : tail, photos };
    try {
      if (editing) {
        await api.patch(`/discussions/${editing.id}`, payload);
        toast.success("Topic updated");
      } else {
        await api.post("/discussions", payload);
        toast.success("Topic posted");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="disc-topic-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit topic" : "New topic"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What do you want to discuss?" data-testid="disc-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="disc-cat-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`disc-cat-option-${k}`}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Aircraft (optional)</Label>
              <Select value={tail} onValueChange={setTail}>
                <SelectTrigger data-testid="disc-tail-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="disc-tail-option-none">None</SelectItem>
                  {aircraft.map((a) => (
                    <SelectItem key={a.tail} value={a.tail} data-testid={`disc-tail-option-${a.tail}`}>{a.tail} · {a.fleet}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Details</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Add context, links, details…" data-testid="disc-body-input" />
            <EmojiBar prefix="disc-dialog" onPick={(e) => setBody((b) => b + e)} />
          </div>
          <div className="space-y-1.5">
            <Label>Photos</Label>
            <PhotoPicker photos={photos} setPhotos={setPhotos} prefix="disc-dialog" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={submit} disabled={busy} data-testid="disc-save-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {editing ? "Save changes" : "Post topic"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Comment({ c, user, onChanged, onReply, isReply }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);
  const mine = c.author_id === user.id;
  const canDelete = mine || user.role === "admin";

  const toggleLike = async () => {
    try {
      await api.post(`/discussions/comments/${c.id}/like`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleHaha = async () => {
    try {
      await api.post(`/discussions/comments/${c.id}/haha`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/discussions/comments/${c.id}`, { body: draft });
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/discussions/comments/${c.id}`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex gap-3" data-testid={`disc-comment-${c.id}`}>
      <div className={`${isReply ? "w-7 h-7" : "w-8 h-8"} rounded-full bg-summit-primary text-white grid place-items-center font-mono text-[11px] shrink-0 mt-0.5`}>
        {initials(c.author_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{c.author_name}</span>
          <span className="font-mono text-[10px] uppercase text-gray-400">{c.author_job_title}</span>
          <span className="font-mono text-[10px] text-gray-400" title={formatMSTDateTime(c.created_at)}>{ago(c.created_at)}</span>
          {c.updated_at && <span className="font-mono text-[10px] text-gray-400 italic">edited</span>}
        </div>
        {editing ? (
          <div className="mt-1.5 space-y-2">
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} data-testid={`disc-comment-edit-input-${c.id}`} />
            <div className="flex gap-2">
              <Button size="sm" className="h-7 bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={saveEdit} data-testid={`disc-comment-edit-save-${c.id}`}>
                <Check className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
              <Button size="sm" variant="outline" className="h-7" onClick={() => { setEditing(false); setDraft(c.body); }}>
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap break-words">{c.body}</p>
            <PhotoStrip photos={c.photos} size="h-24" />
          </>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          <LikeButton small liked={c.liked_by_me} count={c.like_count} onClick={toggleLike} testId={`disc-comment-like-${c.id}`} />
          <HahaButton small reacted={c.haha_by_me} count={c.haha_count} onClick={toggleHaha} testId={`disc-comment-haha-${c.id}`} />
          {onReply && (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gray-400 hover:text-summit-primary transition-colors"
              onClick={onReply}
              data-testid={`disc-comment-reply-${c.id}`}
            >
              <Reply className="w-3.5 h-3.5" /> Reply
            </button>
          )}
          {mine && !editing && (
            <button type="button" className="text-gray-400 hover:text-summit-primary" onClick={() => setEditing(true)} data-testid={`disc-comment-edit-${c.id}`}>
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          )}
          {canDelete && (
            <button type="button" className="text-gray-400 hover:text-rose-600" onClick={remove} data-testid={`disc-comment-delete-${c.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TopicDetail({ topicId, user, onBack, onListDirty }) {
  const [topic, setTopic] = useState(null);
  const [comment, setComment] = useState("");
  const [commentPhotos, setCommentPhotos] = useState([]);
  const [posting, setPosting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/discussions/${topicId}`);
      setTopic(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      onBack();
    }
  };
  useEffect(() => { load(); }, [topicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => { load(); onListDirty(); };

  const toggleLike = async () => {
    try {
      const r = await api.post(`/discussions/${topicId}/like`);
      setTopic((t) => ({ ...t, liked_by_me: r.data.liked, like_count: r.data.like_count }));
      onListDirty();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleHaha = async () => {
    try {
      const r = await api.post(`/discussions/${topicId}/haha`);
      setTopic((t) => ({ ...t, haha_by_me: r.data.reacted, haha_count: r.data.haha_count }));
      onListDirty();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postComment = async () => {
    if (!comment.trim() && commentPhotos.length === 0) return;
    setPosting(true);
    try {
      await api.post(`/discussions/${topicId}/comments`, { body: comment, photos: commentPhotos });
      setComment("");
      setCommentPhotos([]);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  const postReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await api.post(`/discussions/${topicId}/comments`, { body: replyText, parent_id: replyTo });
      setReplyText("");
      setReplyTo(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReplying(false); }
  };

  const removeTopic = async () => {
    if (!window.confirm("Delete this topic and all its comments?")) return;
    try {
      await api.delete(`/discussions/${topicId}`);
      toast.success("Topic deleted");
      onListDirty();
      onBack();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const togglePin = async () => {
    try {
      const r = await api.post(`/discussions/${topicId}/pin`);
      setTopic((t) => ({ ...t, pinned: r.data.pinned }));
      onListDirty();
      toast.success(r.data.pinned ? "Topic pinned to top" : "Topic unpinned");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!topic) {
    return <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>;
  }

  const mine = topic.author_id === user.id;
  const canDelete = mine || user.role === "admin";

  return (
    <div data-testid="disc-detail">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-summit-primary-soft hover:text-summit-primary" data-testid="disc-back-btn">
        <ArrowLeft className="w-4 h-4" /> All topics
      </button>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <CatBadge cat={topic.category} />
              {topic.tail && <TailBadge tail={topic.tail} testId="disc-detail-tail" />}
              {topic.pinned && <PinBadge testId="disc-detail-pinned" />}
            </div>
            <h1 className="font-display text-2xl font-semibold mt-2 break-words" data-testid="disc-detail-title">{topic.title}</h1>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap text-gray-500">
              <span className="text-sm font-medium text-gray-700">{topic.author_name}</span>
              <span className="font-mono text-[10px] uppercase">{topic.author_job_title}</span>
              <span className="font-mono text-[10px]" title={formatMSTDateTime(topic.created_at)}>{ago(topic.created_at)}</span>
              {topic.updated_at && <span className="font-mono text-[10px] italic">edited</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {user.role === "admin" && (
              <Button variant="outline" size="sm" className="h-8" onClick={togglePin} data-testid="disc-pin-btn">
                {topic.pinned ? <PinOff className="w-3.5 h-3.5 mr-1" /> : <Pin className="w-3.5 h-3.5 mr-1" />}
                {topic.pinned ? "Unpin" : "Pin"}
              </Button>
            )}
            {mine && (
              <Button variant="outline" size="sm" className="h-8" onClick={() => setEditOpen(true)} data-testid="disc-edit-topic-btn">
                <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
            )}
            {canDelete && (
              <Button variant="outline" size="sm" className="h-8 text-rose-600 hover:text-rose-700" onClick={removeTopic} data-testid="disc-delete-topic-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
        {topic.body && <p className="text-sm text-gray-700 mt-4 whitespace-pre-wrap break-words" data-testid="disc-detail-body">{topic.body}</p>}
        <PhotoStrip photos={topic.photos} size="h-40" />
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-summit-hairline flex-wrap">
          <LikeButton liked={topic.liked_by_me} count={topic.like_count} onClick={toggleLike} testId="disc-like-btn" />
          <HahaButton reacted={topic.haha_by_me} count={topic.haha_count} onClick={toggleHaha} testId="disc-haha-btn" />
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 font-mono">
            <MessageSquare className="w-3.5 h-3.5" /> {topic.comments.length}
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 font-mono" data-testid="disc-view-count">
            <Eye className="w-3.5 h-3.5" /> {topic.view_count}
          </span>
        </div>
        <div className="mt-3" data-testid="disc-seenby">
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Seen by</div>
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {(topic.viewers || []).map((v) => (
              <span
                key={v.user_id}
                title={`${v.name} · ${formatMSTDateTime(v.at)}`}
                className="w-7 h-7 rounded-full bg-summit-glacier border border-summit-hairline grid place-items-center font-mono text-[10px] text-summit-primary"
                data-testid={`disc-viewer-${v.user_id}`}
              >
                {initials(v.name)}
              </span>
            ))}
            <span className="font-mono text-[11px] text-gray-500 ml-1">
              {(topic.viewers || []).map((v) => v.name).join(" · ")}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <h2 className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
          Comments ({topic.comments.length})
        </h2>
        <div className="mt-3 space-y-5">
          {topic.comments.length === 0 && (
            <p className="text-sm text-gray-400">No comments yet — be the first to reply.</p>
          )}
          {topic.comments.filter((c) => !c.parent_id).map((c) => {
            const replies = topic.comments.filter((r) => r.parent_id === c.id);
            return (
              <div key={c.id}>
                <Comment c={c} user={user} onChanged={refresh} onReply={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyText(""); }} />
                {replies.length > 0 && (
                  <div className="ml-11 mt-3 space-y-3 border-l-2 border-summit-hairline pl-4" data-testid={`disc-replies-${c.id}`}>
                    {replies.map((r) => (
                      <Comment key={r.id} c={r} user={user} onChanged={refresh} isReply />
                    ))}
                  </div>
                )}
                {replyTo === c.id && (
                  <div className="ml-11 mt-2.5 flex gap-2" data-testid={`disc-reply-composer-${c.id}`}>
                    <div className="flex-1">
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={2}
                        placeholder={`Reply to ${c.author_name}…`}
                        autoFocus
                        data-testid="disc-reply-input"
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <Button size="sm" variant="outline" className="h-7" onClick={() => setReplyTo(null)} data-testid="disc-reply-cancel">
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 bg-summit-primary hover:bg-summit-primary-soft text-white"
                          onClick={postReply}
                          disabled={replying || !replyText.trim()}
                          data-testid="disc-reply-submit"
                        >
                          {replying ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Reply className="w-3.5 h-3.5 mr-1" />}
                          Reply
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex gap-3">
          <div className="w-8 h-8 rounded-full bg-summit-primary text-white grid place-items-center font-mono text-[11px] shrink-0 mt-0.5">
            {initials(user.display_name)}
          </div>
          <div className="flex-1">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              placeholder="Write a comment…"
              data-testid="disc-comment-input"
            />
            <div className="flex justify-between items-center mt-2 gap-2 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <EmojiBar prefix="disc-comment" onPick={(e) => setComment((c) => c + e)} />
                <PhotoPicker photos={commentPhotos} setPhotos={setCommentPhotos} prefix="disc-comment" />
              </div>
              <Button
                size="sm"
                className="bg-summit-primary hover:bg-summit-primary-soft text-white"
                onClick={postComment}
                disabled={posting || (!comment.trim() && commentPhotos.length === 0)}
                data-testid="disc-comment-submit"
              >
                {posting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Comment
              </Button>
            </div>
          </div>
        </div>
      </div>

      <TopicDialog open={editOpen} onClose={() => setEditOpen(false)} onSaved={refresh} editing={topic} />
    </div>
  );
}

export default function Discussion() {
  const { user } = useAuth();
  const [topics, setTopics] = useState(null);
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [tailFilter, setTailFilter] = useState("all");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const t = searchParams.get("topic");
    if (t) setSelected(t);
  }, [searchParams]);

  useEffect(() => { setPage(1); }, [cat, search, sort, tailFilter]);

  const load = async () => {
    try {
      const r = await api.get("/discussions");
      setTopics(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const m = { all: (topics || []).length };
    Object.keys(CATS).forEach((k) => { m[k] = (topics || []).filter((t) => t.category === k).length; });
    return m;
  }, [topics]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = (topics || []).filter((t) => cat === "all" || t.category === cat);
    if (tailFilter !== "all") out = out.filter((t) => t.tail === tailFilter);
    if (q) out = out.filter((t) => [t.title, t.body_preview, t.author_name].some((v) => (v || "").toLowerCase().includes(q)));
    const key = { views: "view_count", comments: "comment_count", likes: "like_count" }[sort];
    return [...out].sort((a, b) => {
      if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
      if (!!a.viewed_by_me !== !!b.viewed_by_me) return a.viewed_by_me ? 1 : -1;
      if (key) return (b[key] || 0) - (a[key] || 0);
      return (b.last_activity_at || "").localeCompare(a.last_activity_at || "");
    });
  }, [topics, cat, search, sort, tailFilter]);

  const tailOptions = useMemo(
    () => [...new Set((topics || []).map((t) => t.tail).filter(Boolean))].sort(),
    [topics]
  );

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(() => visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE), [visible, safePage]);

  if (!user) return null;

  return (
    <div className="w-full mx-auto px-4 sm:px-[15%] py-8" data-testid="discussion-page">
      {selected ? (
        <TopicDetail
          topicId={selected}
          user={user}
          onBack={() => {
            setSelected(null);
            if (searchParams.get("topic")) setSearchParams({}, { replace: true });
          }}
          onListDirty={load}
        />
      ) : (
        <>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl font-semibold tracking-tight flex items-center gap-2.5">
                <MessagesSquare className="w-7 h-7 text-summit-primary" /> Discussion
              </h1>
              <p className="font-mono text-[11px] uppercase tracking-widest text-gray-500 mt-1">Team forum · everyone can post · topics auto-clear after 3 months idle</p>
            </div>
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" onClick={() => setCreateOpen(true)} data-testid="disc-new-topic-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Topic
            </Button>
          </div>

          <div className="flex items-center gap-2 mt-6 flex-wrap">
            {[["all", { label: "All" }], ...Object.entries(CATS)].map(([k, v]) => (
              <button
                key={k}
                type="button"
                onClick={() => setCat(k)}
                data-testid={`disc-cat-chip-${k}`}
                className={`px-3 py-1 rounded-full border font-mono text-[11px] uppercase tracking-widest transition-colors ${
                  cat === k ? "bg-summit-primary border-summit-primary text-white" : "border-summit-hairline text-gray-600 hover:bg-summit-glacier"
                }`}
              >
                {v.label} <span className="opacity-60">{counts[k] ?? 0}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search topics, authors…"
                className="pl-8 h-9"
                data-testid="disc-search-input"
              />
            </div>
            {tailOptions.length > 0 && (
              <Select value={tailFilter} onValueChange={setTailFilter}>
                <SelectTrigger className="w-40 h-9" data-testid="disc-tail-filter">
                  <Plane className="w-3.5 h-3.5 mr-1 text-gray-400 shrink-0" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="disc-tail-filter-all">All aircraft</SelectItem>
                  {tailOptions.map((tl) => (
                    <SelectItem key={tl} value={tl} data-testid={`disc-tail-filter-${tl}`}>{tl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48 h-9" data-testid="disc-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="latest" data-testid="disc-sort-latest">Latest activity</SelectItem>
                <SelectItem value="views" data-testid="disc-sort-views">Most viewed</SelectItem>
                <SelectItem value="comments" data-testid="disc-sort-comments">Most commented</SelectItem>
                <SelectItem value="likes" data-testid="disc-sort-likes">Most liked</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-5 space-y-2.5" data-testid="disc-topic-list">
            {topics === null && (
              <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
            )}
            {topics !== null && visible.length === 0 && (
              <div className="border border-dashed border-summit-hairline rounded-lg py-14 text-center" data-testid="disc-empty">
                <MessagesSquare className="w-8 h-8 mx-auto text-gray-300" />
                <p className="text-sm text-gray-500 mt-3">No topics yet. Start the conversation!</p>
              </div>
            )}
            {paged.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setSelected(t.id);
                  setTopics((list) => (list || []).map((x) => (x.id === t.id ? { ...x, viewed_by_me: true } : x)));
                }}
                className="w-full text-left border border-summit-hairline rounded-lg bg-white px-4 py-3.5 hover:border-summit-primary/40 hover:shadow-sm transition-all flex items-start gap-3"
                data-testid={`disc-topic-row-${t.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-summit-primary text-white grid place-items-center font-mono text-[11px] shrink-0 mt-0.5">
                  {initials(t.author_name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CatBadge cat={t.category} />
                    {t.tail && <TailBadge tail={t.tail} testId={`disc-row-tail-${t.id}`} />}
                    {t.pinned && <PinBadge testId={`disc-pinned-${t.id}`} />}
                    {!t.viewed_by_me && <span className="w-2 h-2 rounded-full bg-sky-500" data-testid={`disc-unread-${t.id}`} />}
                  </div>
                  <div className="font-medium text-[15px] mt-1 truncate">{t.title}</div>
                  {t.body_preview && <div className="text-sm text-gray-500 truncate mt-0.5">{t.body_preview}</div>}
                  <div className="flex items-center gap-2 mt-1.5 font-mono text-[10px] text-gray-400 uppercase">
                    <span>{t.author_name}</span>
                    <span>·</span>
                    <span title={formatMSTDateTime(t.created_at)}>{ago(t.last_activity_at)}</span>
                    {t.edited && <span className="italic normal-case">edited</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0 font-mono text-[11px] text-gray-500 mt-1">
                  <span className={`inline-flex items-center gap-1 ${t.liked_by_me ? "text-sky-600" : ""}`} data-testid={`disc-row-likes-${t.id}`}>
                    <ThumbsUp className={`w-3.5 h-3.5 ${t.liked_by_me ? "fill-sky-500 text-sky-600" : ""}`} /> {t.like_count}
                  </span>
                  <span className={`inline-flex items-center gap-1 ${t.haha_by_me ? "text-amber-600" : ""}`} data-testid={`disc-row-hahas-${t.id}`}>
                    <Laugh className="w-3.5 h-3.5" /> {t.haha_count}
                  </span>
                  <span className="inline-flex items-center gap-1" data-testid={`disc-row-comments-${t.id}`}>
                    <MessageSquare className="w-3.5 h-3.5" /> {t.comment_count}
                  </span>
                  <span className="inline-flex items-center gap-1" data-testid={`disc-row-views-${t.id}`}>
                    <Eye className="w-3.5 h-3.5" /> {t.view_count}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-5" data-testid="disc-pager">
              <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="disc-page-prev">
                Prev
              </Button>
              <span className="font-mono text-[11px] uppercase tracking-widest text-gray-500" data-testid="disc-page-label">
                Page {safePage} of {totalPages}
              </span>
              <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="disc-page-next">
                Next
              </Button>
            </div>
          )}

          <TopicDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} />
        </>
      )}
    </div>
  );
}

